import { useAppSelector } from '../../../hooks/useReduxToolkit';
import { getApolloSessionDetails } from '../../../features/apolloSession/apolloSession.selectors';
import { TrackedEvents, track } from '../../../features/tracking';
import { useUserSubscription } from '../../../hooks/useUserSubscription';
import { PhotoCaptureTimer } from './photoCaptureTimer';
import { type PhotoCaptureRequestStatus } from '../../../services/photoCapture';

const updatePhotoCaptureTimer = (status: PhotoCaptureRequestStatus): void => {
  switch (status) {
    case 'requested': {
      PhotoCaptureTimer.start(status, Date.now());
      break;
    }
    case 'rejected':
    case 'uploaded':
    case 'error': {
      PhotoCaptureTimer.finish(status);
      break;
    }
    default: {
      PhotoCaptureTimer.step(status);
    }
  }
};

type PhotoCaptureEvents = {
  sendPhotoCaptureStartedEvent: () => void;
  sendPhotoCapturePhotoTakenEvent: () => void;
  sendPhotoCaptureRetakeClickedEvent: () => void;
  sendPhotoCaptureDownloadClickedEvent: () => void;
  updatePhotoCaptureTimer: (status: PhotoCaptureRequestStatus) => void;
};

export const usePhotoCaptureEvents = (): PhotoCaptureEvents => {
  const { userId, dbSessionId, role, roomId, institutionId } = useAppSelector(
    getApolloSessionDetails,
  );

  const { plan } = useUserSubscription();

  const metaProps = {
    userId,
    sessionId: dbSessionId,
    userType: role,
    roomId,
    institutionId,
    planType: plan,
  };

  const sendPhotoCaptureStartedEvent = (): void => {
    void track(TrackedEvents.PhotoCaptureStarted, {
      ...metaProps,
    });
  };

  const sendPhotoCapturePhotoTakenEvent = (): void => {
    void track(TrackedEvents.PhotoCapturePhotoTaken, {
      ...metaProps,
    });
  };

  const sendPhotoCaptureRetakeClickedEvent = (): void => {
    void track(TrackedEvents.PhotoCaptureRetakeClicked, {
      ...metaProps,
    });
  };

  const sendPhotoCaptureDownloadClickedEvent = (): void => {
    void track(TrackedEvents.PhotoCaptureDownloadClicked, {
      ...metaProps,
    });
  };

  return {
    sendPhotoCaptureStartedEvent,
    sendPhotoCapturePhotoTakenEvent,
    sendPhotoCaptureRetakeClickedEvent,
    sendPhotoCaptureDownloadClickedEvent,
    updatePhotoCaptureTimer,
  };
};
